import {
  estimatedCookedWeight,
  RECIPE_COOKING,
  RECIPE_COOKING_ORDER,
  type RecipeCooking,
} from '@gramas/shared'
import { NumberField, Pill } from '@gramas/ui'

/**
 * The finished weight, and the question that gets you a first guess at it.
 *
 * Every portion ever logged from a recipe is this number divided into the
 * macros, so getting it wrong scales the whole batch. Defaulting it to the raw
 * ingredient total — which is what happened before — quietly assumes nothing
 * evaporated, and then a cake that spent an hour in an oven reads a tenth light
 * in every slice while the screen says nothing about it.
 *
 * So the screen asks. Not to store how the dish was cooked — once it has been
 * weighed the method tells you nothing the number does not — but because "was
 * it baked or was it a salad" is a question anyone can answer in one tap, and
 * it is the difference between a defensible estimate and an assumption nobody
 * made on purpose.
 */
export function CookedWeightField({
  rawTotalG,
  method,
  onMethodChange,
  value,
  onChange,
}: {
  rawTotalG: number
  method: RecipeCooking
  onMethodChange: (method: RecipeCooking, suggested: number | null) => void
  value: number | null
  onChange: (value: number | null) => void
}) {
  const profile = RECIPE_COOKING[method]
  const suggested = estimatedCookedWeight(rawTotalG, method)
  const estimated = suggested != null && value === suggested

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5">
        <span className="text-sm text-dim">Kaip gaminote?</span>
        <div className="flex flex-wrap gap-1.5">
          {RECIPE_COOKING_ORDER.map((option) => (
            <Pill
              key={option}
              active={option === method}
              onClick={() => onMethodChange(option, estimatedCookedWeight(rawTotalG, option))}
            >
              {RECIPE_COOKING[option].label}
            </Pill>
          ))}
        </div>
      </div>

      <NumberField label="Gatavo patiekalo svoris" suffix="g" value={value} onChange={onChange} />
      <p className="text-xs text-mute">
        {profile.hint}{' '}
        {estimated
          ? `Spėjama pagal ${Math.round(rawTotalG)} g žalių produktų — pasverkite puodą su gaminiu ir atimkite tuščio puodo svorį, tada skaičiai bus tikslūs.`
          : 'Tiksliausia — pasverti puodą su gaminiu ir atimti tuščio puodo svorį.'}
      </p>
    </div>
  )
}
